/**
 * OVERVOLT — Puzzle level-select overlay (Sprint 30 / puzzle-impl-s30).
 *
 * A single-canvas modal that lists the authored puzzle curriculum (see
 * `src/game/puzzles.ts`) as a grid of cards. Mirrors the patterns in
 * `profileScreen.ts` — a dimmed backdrop, a centred panel, level cards
 * stamped with solved-status / par-medal pips and the player's per-axis
 * bests (towers / gold / power, see PUZZLE_DESIGN.md §5).
 *
 * Render-only. The overlay carries no GameState; it consults
 * `loadPuzzleBests()` for the persisted bests grid (`overvolt.puzzleBests`)
 * and the `PUZZLES` curriculum for the level list. Clicks are routed by the
 * `puzzleSelectHit(x, y)` hit-test (returned id is the level to load) — the
 * orchestrator wires it into `Game.handleClick` only when the overlay is
 * open (a tiny follow-up — out of MVP scope this sprint, see §puzzle in
 * docs/GAME_DESIGN.md).
 *
 * The overlay is exported as **render + hit-test only**. The orchestrator
 * (`Game.ts`) owns the open/close state and the hot-key binding so the
 * input-routing precedence (Resume prompt > Profile > onboarding > overlay)
 * stays in one place. MVP convention.
 */
import { CONFIG, type TowerType } from '../config';
import { loadPuzzleBests, type PuzzleBest, type PuzzleBestsMap } from '../game/puzzle';
import { PUZZLES, type PuzzleLevel, getPuzzleLevel } from '../game/puzzles';
import { t } from '../i18n';

// ----------------------------------------------------------------------------
// Geometry — canvas-space rectangles, pixel-perfect placement.
// ----------------------------------------------------------------------------

// Sprint 48 (puzzle-extra-s48) — laid out for 10 levels (P0-P9). The
// pre-Sprint-48 layout was 2 cols × 130-tall cards centred on a 480-tall
// panel; 4-row overflow at 7 levels was already nominal (the bottom row
// clipped at the canvas's 596-px floor) and 10 levels made it terminal.
// 3 cols × 4 rows = 12 slots — 10 used, 2 empty — keeps the cards readable
// and the panel inside the canvas. Card width drops 320 → 210 (still
// comfortably fits the title + 4 text lines); height drops 130 → 95
// (trims the bottom whitespace, no per-card content is removed).
//
// Sprint 74b (puzzle-rework-s74b) — at 17 levels (10 original + 7 hard
// tier) the 12-slot grid overflows again. Pagination: 12 levels per page,
// Prev / Next buttons, page indicator. The unlock chain is unchanged so a
// fresh player always starts on page 1 (with P0 unlocked); page 2 reveals
// itself only once enough levels are solved to make it relevant.
const PANEL = { x: 60, y: 30, w: 680, h: 540 } as const;
const CARD_COLS = 3;
const CARD_W = 210;
const CARD_H = 95;
const CARD_GUTTER = 12;
const CARDS_ORIGIN = { x: PANEL.x + 14, y: PANEL.y + 78 } as const;
const CLOSE_BTN = { x: PANEL.x + PANEL.w - 80, y: PANEL.y + 14, w: 64, h: 28 } as const;

// Sprint 74b — pagination chrome. Buttons live at the bottom of the
// panel; the page indicator centres between them.
const LEVELS_PER_PAGE = CARD_COLS * 4; // 3 cols × 4 rows = 12 cards/page.
const PAGE_BAR_Y = PANEL.y + PANEL.h - 36;
const PREV_BTN = { x: PANEL.x + 24, y: PAGE_BAR_Y, w: 80, h: 24 } as const;
const NEXT_BTN = { x: PANEL.x + PANEL.w - 104, y: PAGE_BAR_Y, w: 80, h: 24 } as const;

/**
 * Sprint 74b — the currently-visible page, module-local state. Reset to 0
 * when the overlay opens fresh; the page index is clamped on every render
 * so a level-set shrink (defensive) doesn't leave the user stranded.
 */
let currentPage = 0;
const pageCount = (): number => Math.max(1, Math.ceil(PUZZLES.length / LEVELS_PER_PAGE));
const clampPage = (): void => {
  if (currentPage < 0) currentPage = 0;
  if (currentPage >= pageCount()) currentPage = pageCount() - 1;
};

/** Reset the overlay's pagination state. Called by the orchestrator on open. */
export function resetPuzzleSelectPage(): void {
  currentPage = 0;
}

const COLORS = {
  scrim: 'rgba(6, 10, 22, 0.78)',
  panel: '#0c1322',
  panelStroke: '#2de2e6',
  cardBg: '#10182a',
  cardStroke: 'rgba(45, 226, 230, 0.55)',
  cardLocked: 'rgba(80, 92, 120, 0.35)',
  text: '#dce7f2',
  muted: '#7d8aa3',
  solved: '#6cf2a0',
  medal: '#f7d51d',
  medalDim: 'rgba(247, 213, 29, 0.25)',
  accent: '#2de2e6',
} as const;

// ----------------------------------------------------------------------------
// Hit-testing
// ----------------------------------------------------------------------------

/** Hit-test result — what (if anything) was clicked in the overlay. */
export type PuzzleSelectHit =
  | { kind: 'close' }
  | { kind: 'level'; id: string }
  | { kind: 'page'; delta: -1 | 1 }
  | null;

/** Card rectangle for the `slot`-th visible card on the current page. */
function cardRect(slot: number): { x: number; y: number; w: number; h: number } {
  const col = slot % CARD_COLS;
  const row = Math.floor(slot / CARD_COLS);
  return {
    x: CARDS_ORIGIN.x + col * (CARD_W + CARD_GUTTER),
    y: CARDS_ORIGIN.y + row * (CARD_H + CARD_GUTTER),
    w: CARD_W,
    h: CARD_H,
  };
}

/** Hit-test the overlay. Returns `null` for a click in the dead zone. */
export function puzzleSelectHit(x: number, y: number): PuzzleSelectHit {
  if (inRect(x, y, CLOSE_BTN)) return { kind: 'close' };
  clampPage();
  if (pageCount() > 1) {
    if (inRect(x, y, PREV_BTN) && currentPage > 0) {
      currentPage -= 1;
      return { kind: 'page', delta: -1 };
    }
    if (inRect(x, y, NEXT_BTN) && currentPage < pageCount() - 1) {
      currentPage += 1;
      return { kind: 'page', delta: 1 };
    }
  }
  const start = currentPage * LEVELS_PER_PAGE;
  const end = Math.min(PUZZLES.length, start + LEVELS_PER_PAGE);
  for (let i = start; i < end; i += 1) {
    const r = cardRect(i - start);
    if (inRect(x, y, r)) {
      if (!isUnlocked(i, loadPuzzleBests())) return null;
      return { kind: 'level', id: PUZZLES[i]!.id };
    }
  }
  return null;
}

function inRect(x: number, y: number, r: { x: number; y: number; w: number; h: number }): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

// ----------------------------------------------------------------------------
// Unlock chain — a linear curriculum (PUZZLE_DESIGN.md §4.3).
// ----------------------------------------------------------------------------

/**
 * Level `index` is unlocked when:
 *   - It is the first level (always unlocked); OR
 *   - The previous level has been solved.
 */
export function isUnlocked(index: number, bests: PuzzleBestsMap): boolean {
  if (index === 0) return true;
  const prev = PUZZLES[index - 1];
  if (!prev) return false;
  return bests[prev.id]?.solved === true;
}

// ----------------------------------------------------------------------------
// Drawing
// ----------------------------------------------------------------------------

/** Draw the overlay over the existing canvas. Caller dims the world first. */
export function drawPuzzleSelect(ctx: CanvasRenderingContext2D): void {
  const bests = loadPuzzleBests();
  // Scrim.
  ctx.fillStyle = COLORS.scrim;
  ctx.fillRect(0, 0, CONFIG.canvas.width, CONFIG.canvas.height);
  // Panel.
  ctx.fillStyle = COLORS.panel;
  ctx.fillRect(PANEL.x, PANEL.y, PANEL.w, PANEL.h);
  ctx.strokeStyle = COLORS.panelStroke;
  ctx.lineWidth = 2;
  ctx.strokeRect(PANEL.x + 0.5, PANEL.y + 0.5, PANEL.w - 1, PANEL.h - 1);
  // Title. Sprint 69 — reuse-bug fix: `puzzle.label` already in the
  // catalogue. Subtitle + Close route through the new `puzzle.select.*`.
  ctx.fillStyle = COLORS.text;
  ctx.font = '22px "Chakra Petch", system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(`${t('puzzle.label')} MODE`, PANEL.x + 24, PANEL.y + 22);
  ctx.font = '12px "Share Tech Mono", ui-monospace, monospace';
  ctx.fillStyle = COLORS.muted;
  ctx.fillText(t('puzzle.select.subtitle'), PANEL.x + 24, PANEL.y + 50);
  // Close button.
  drawButton(ctx, CLOSE_BTN, t('puzzle.select.close'));
  // Cards (paginated). Slice the level set down to the current page; the
  // card slot index (0..LEVELS_PER_PAGE-1) is decoupled from the absolute
  // level index so cardRect stays oblivious to pagination.
  clampPage();
  const start = currentPage * LEVELS_PER_PAGE;
  const end = Math.min(PUZZLES.length, start + LEVELS_PER_PAGE);
  for (let i = start; i < end; i += 1) {
    drawCard(ctx, i, i - start, PUZZLES[i]!, bests, isUnlocked(i, bests));
  }
  // Pagination chrome — only when there's more than one page.
  if (pageCount() > 1) {
    drawButton(ctx, PREV_BTN, currentPage > 0 ? '← Prev' : '—');
    drawButton(ctx, NEXT_BTN, currentPage < pageCount() - 1 ? 'Next →' : '—');
    ctx.fillStyle = COLORS.muted;
    ctx.font = '12px "Share Tech Mono", ui-monospace, monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      `Page ${currentPage + 1} / ${pageCount()}`,
      PANEL.x + PANEL.w / 2,
      PAGE_BAR_Y + 12,
    );
  }
}

function drawCard(
  ctx: CanvasRenderingContext2D,
  index: number,
  slot: number,
  level: PuzzleLevel,
  bests: PuzzleBestsMap,
  unlocked: boolean,
): void {
  const r = cardRect(slot);
  const best = bests[level.id];
  ctx.fillStyle = COLORS.cardBg;
  ctx.fillRect(r.x, r.y, r.w, r.h);
  ctx.strokeStyle = unlocked ? COLORS.cardStroke : COLORS.cardLocked;
  ctx.lineWidth = 1.5;
  ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  // Card title row: id + name. Sprint 69 — route the per-level name +
  // hint through `t('puzzle.levels.<id>.…')` (those keys already existed
  // in en.ts; the renderer was reading the English `level.name` /
  // `level.hint` literal from puzzles.ts).
  const levelName = t(`puzzle.levels.${level.id}.name`) || level.name;
  const levelHint = t(`puzzle.levels.${level.id}.hint`) || level.hint;
  ctx.fillStyle = unlocked ? COLORS.text : COLORS.muted;
  ctx.font = '13px "Chakra Petch", system-ui, sans-serif';
  ctx.fillText(t('puzzle.select.cardTitle', { index, name: levelName }), r.x + 8, r.y + 6);

  if (!unlocked) {
    ctx.fillStyle = COLORS.muted;
    ctx.font = '10px "Share Tech Mono", ui-monospace, monospace';
    ctx.fillText(t('puzzle.select.lockedCard'), r.x + 8, r.y + 28);
    return;
  }

  // Status line.
  const solved = best?.solved === true;
  ctx.fillStyle = solved ? COLORS.solved : COLORS.accent;
  ctx.font = '10px "Share Tech Mono", ui-monospace, monospace';
  ctx.fillText(t(solved ? 'puzzle.select.solved' : 'puzzle.select.unsolved'), r.x + 8, r.y + 25);

  // Par-medal pips (3 axes).
  drawMedalRow(ctx, r.x + 75, r.y + 25, level, best);

  // Best-stats line.
  ctx.fillStyle = COLORS.muted;
  ctx.font = '10px "Share Tech Mono", ui-monospace, monospace';
  if (best?.solved) {
    const tt = Number.isFinite(best.bestTowers) ? best.bestTowers : '—';
    const tg = Number.isFinite(best.bestGold) ? best.bestGold : '—';
    const tp = Number.isFinite(best.bestPower) ? best.bestPower : '—';
    ctx.fillText(
      t('puzzle.select.bestLine', { towers: tt, gold: tg, power: tp }),
      r.x + 8,
      r.y + 44,
    );
  } else {
    ctx.fillText(t('puzzle.select.noSolve'), r.x + 8, r.y + 44);
  }
  // Hint blurb (single line, truncated to fit the 210-px card).
  ctx.fillStyle = COLORS.text;
  ctx.font = '10px "Share Tech Mono", ui-monospace, monospace';
  const hint = levelHint.length > 38 ? `${levelHint.slice(0, 35)}…` : levelHint;
  ctx.fillText(hint, r.x + 8, r.y + 60);

  // Tower-list line.
  ctx.fillStyle = COLORS.muted;
  ctx.font = '9px "Share Tech Mono", ui-monospace, monospace';
  ctx.fillText(
    t('puzzle.select.allowed', { list: level.allowedTowers.join(' · ') }),
    r.x + 8,
    r.y + 79,
  );
}

/**
 * Three-pip par medal row (towers / gold / power) with the authored par
 * trio rendered as a numeric label beside the pips. Sprint 30 shipped pips
 * only; Sprint 40 (puzzle-polish-s40) surfaces the par numbers always — a
 * locked / unsolved level still shows the target so the player knows what
 * the Zachtronics-style three-axis chase is BEFORE committing to a solve.
 *
 * Layout (anchored at `x, y`):
 *   ◆ ◆ ◆   5t · 440g · 31p
 *  ^ ^ ^   ^---------------^
 *  pips    par numbers
 *
 * Pre-solve: pips render dim; numbers render in muted text.
 * Post-solve: pips render filled iff `best <= par` on that axis; numbers
 * stay in muted text (the player reads their actual best from the
 * `Best:` line below, not from this row).
 */
function drawMedalRow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  level: PuzzleLevel,
  best: PuzzleBest | undefined,
): void {
  // Levels without authored par (very rare; the §1.2 default is to author
  // par on every shipped level) render the historical em-dash row.
  if (!level.par) {
    ctx.fillStyle = COLORS.muted;
    ctx.font = '10px "Share Tech Mono", ui-monospace, monospace';
    ctx.fillText(t('puzzle.select.parPlaceholder'), x, y);
    return;
  }
  const earned = best?.solved
    ? {
        towers: best.bestTowers <= level.par.towers,
        gold: best.bestGold <= level.par.gold,
        power: best.bestPower <= level.par.power,
      }
    : { towers: false, gold: false, power: false };
  // Pip row first (compact: three 10-px discs on 16-px centres).
  drawPip(ctx, x, y + 6, earned.towers);
  drawPip(ctx, x + 16, y + 6, earned.gold);
  drawPip(ctx, x + 32, y + 6, earned.power);
  // Par numbers — always visible. Reads naturally as "the bar to clear".
  ctx.fillStyle = COLORS.muted;
  ctx.font = '10px "Share Tech Mono", ui-monospace, monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(
    t('puzzle.select.parRow', {
      towers: level.par.towers,
      gold: level.par.gold,
      power: level.par.power,
    }),
    x + 46,
    y + 6,
  );
  // Restore the card's outer baseline so subsequent drawText calls in
  // `drawCard` don't pick up our `middle` baseline.
  ctx.textBaseline = 'top';
}

function drawPip(ctx: CanvasRenderingContext2D, cx: number, cy: number, filled: boolean): void {
  ctx.beginPath();
  ctx.arc(cx, cy, 5, 0, Math.PI * 2);
  ctx.fillStyle = filled ? COLORS.medal : COLORS.medalDim;
  ctx.fill();
}

function drawButton(
  ctx: CanvasRenderingContext2D,
  box: { x: number; y: number; w: number; h: number },
  label: string,
): void {
  ctx.fillStyle = '#101a30';
  ctx.fillRect(box.x, box.y, box.w, box.h);
  ctx.strokeStyle = COLORS.accent;
  ctx.lineWidth = 1.5;
  ctx.strokeRect(box.x + 0.5, box.y + 0.5, box.w - 1, box.h - 1);
  ctx.fillStyle = COLORS.accent;
  ctx.font = '12px "Chakra Petch", system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, box.x + box.w / 2, box.y + box.h / 2);
}

// ============================================================================
// Sprint 63 (puzzle-ux-briefing-s63) — pre-run briefing card.
// ----------------------------------------------------------------------------
// A modal card stamped over the playfield the moment a puzzle level loads.
// Carries:
//   - the level's index + name and authored one-line "aha" intent (briefing
//     field on `PuzzleLevel`, populated for every shipped level);
//   - the constraint block: gold budget, wave count, allowed-tower icons,
//     plus forbidden-tower icons greyed out;
//   - the win condition as a plain sentence;
//   - Start / Cancel buttons. Start dismisses the card, leaving the player on
//     the playfield in build phase; Cancel exits puzzle mode back to the
//     level-select overlay (routed by the orchestrator).
//
// The card is render + hit-test only — same shape as `puzzleSelectHit`. The
// orchestrator (`Game.ts`) owns the open/close state via the new
// `state.puzzleBriefingPending` flag and absorbs every click while the card
// is up (the briefing is modal, like the level-select overlay above it).
// ============================================================================

const BRIEFING_PANEL = { x: 100, y: 80, w: 600, h: 440 } as const;
const BRIEFING_START_BTN = {
  x: BRIEFING_PANEL.x + BRIEFING_PANEL.w / 2 + 12,
  y: BRIEFING_PANEL.y + BRIEFING_PANEL.h - 56,
  w: 170,
  h: 40,
} as const;
const BRIEFING_CANCEL_BTN = {
  x: BRIEFING_PANEL.x + BRIEFING_PANEL.w / 2 - 12 - 170,
  y: BRIEFING_PANEL.y + BRIEFING_PANEL.h - 56,
  w: 170,
  h: 40,
} as const;

/** Hit-test result for the briefing card — Start / Cancel / null (dead zone). */
export type PuzzleBriefingHit = { kind: 'start' } | { kind: 'cancel' } | null;

/**
 * Hit-test a click against the puzzle briefing card. Returns `'start'` to
 * dismiss and enter the build phase, `'cancel'` to bounce back to the
 * level-select overlay, or `null` for a click in the dead zone (absorbed —
 * the card is modal).
 */
export function puzzleBriefingHit(x: number, y: number): PuzzleBriefingHit {
  if (inRect(x, y, BRIEFING_START_BTN)) return { kind: 'start' };
  if (inRect(x, y, BRIEFING_CANCEL_BTN)) return { kind: 'cancel' };
  return null;
}

/**
 * Draw the pre-run briefing card for puzzle `levelId`. No-op when the id does
 * not resolve to a shipped level (defensive; the orchestrator never invokes
 * this without a loaded level, but the guard keeps the render path
 * cheap-and-safe).
 */
export function drawPuzzleBriefing(
  ctx: CanvasRenderingContext2D,
  levelId: string,
): void {
  const level = getPuzzleLevel(levelId);
  if (!level) return;
  const index = PUZZLES.findIndex((l) => l.id === levelId);

  // Scrim + panel.
  ctx.fillStyle = COLORS.scrim;
  ctx.fillRect(0, 0, CONFIG.canvas.width, CONFIG.canvas.height);
  ctx.fillStyle = COLORS.panel;
  ctx.fillRect(BRIEFING_PANEL.x, BRIEFING_PANEL.y, BRIEFING_PANEL.w, BRIEFING_PANEL.h);
  ctx.strokeStyle = COLORS.panelStroke;
  ctx.lineWidth = 2;
  ctx.strokeRect(
    BRIEFING_PANEL.x + 0.5,
    BRIEFING_PANEL.y + 0.5,
    BRIEFING_PANEL.w - 1,
    BRIEFING_PANEL.h - 1,
  );

  // Header — "P3 · Funhouse Mirror" + eyebrow.
  const cx = BRIEFING_PANEL.x + BRIEFING_PANEL.w / 2;
  const top = BRIEFING_PANEL.y;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.font = '10px "Share Tech Mono", ui-monospace, monospace';
  ctx.fillStyle = COLORS.muted;
  ctx.fillText('PUZZLE BRIEFING', cx, top + 18);
  ctx.font = '26px "Chakra Petch", system-ui, sans-serif';
  ctx.fillStyle = COLORS.text;
  const title = `P${index >= 0 ? index : '?'}  ·  ${level.briefing.name}`;
  ctx.fillText(title, cx, top + 34);

  // Body — one-line "aha". Wrapped to the panel width with a 36-px margin.
  const bodyY = top + 80;
  ctx.font = '14px "Chakra Petch", system-ui, sans-serif';
  ctx.fillStyle = COLORS.accent;
  ctx.textAlign = 'left';
  const innerX = BRIEFING_PANEL.x + 36;
  const innerW = BRIEFING_PANEL.w - 72;
  const oneLineLines = wrapToWidth(ctx, level.briefing.oneLine, innerW);
  let cursorY = bodyY;
  for (const line of oneLineLines) {
    ctx.fillText(line, innerX, cursorY);
    cursorY += 19;
  }
  cursorY += 6;

  // Divider.
  ctx.strokeStyle = 'rgba(45, 226, 230, 0.25)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(innerX, cursorY);
  ctx.lineTo(innerX + innerW, cursorY);
  ctx.stroke();
  cursorY += 14;

  // Constraints block — gold budget, wave count, allowed / forbidden towers.
  ctx.font = '10px "Share Tech Mono", ui-monospace, monospace';
  ctx.fillStyle = COLORS.muted;
  ctx.fillText('CONSTRAINTS', innerX, cursorY);
  cursorY += 16;
  ctx.font = '13px "Share Tech Mono", ui-monospace, monospace';
  ctx.fillStyle = COLORS.text;
  ctx.fillText(`Gold budget · ${level.goldBudget}g`, innerX, cursorY);
  ctx.fillText(
    `Waves · ${level.wave.groups.length > 0 ? 1 : 0} of 1`,
    innerX + innerW / 2,
    cursorY,
  );
  cursorY += 22;

  // Tower-icon row: allowed (lit) + forbidden (greyed).
  const allowedSet = new Set<TowerType>(level.allowedTowers);
  const allTowers: readonly TowerType[] = ['pulse', 'arc', 'pylon', 'cryo', 'mortar', 'sniper'];
  ctx.font = '10px "Share Tech Mono", ui-monospace, monospace';
  ctx.fillStyle = COLORS.muted;
  ctx.fillText('TOWERS', innerX, cursorY);
  cursorY += 14;
  let chipX = innerX;
  const chipY = cursorY;
  const CHIP_W = 64;
  const CHIP_H = 38;
  const CHIP_GAP = 6;
  for (const type of allTowers) {
    const allowed = allowedSet.has(type);
    const color = CONFIG.towers[type].color;
    ctx.fillStyle = allowed ? '#10182a' : 'rgba(20, 28, 42, 0.6)';
    ctx.fillRect(chipX, chipY, CHIP_W, CHIP_H);
    ctx.strokeStyle = allowed ? color : 'rgba(80, 92, 120, 0.5)';
    ctx.lineWidth = allowed ? 1.5 : 1;
    ctx.strokeRect(chipX + 0.5, chipY + 0.5, CHIP_W - 1, CHIP_H - 1);
    ctx.fillStyle = allowed ? color : 'rgba(125, 138, 163, 0.6)';
    ctx.font = '11px "Chakra Petch", system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(CONFIG.towers[type].label, chipX + CHIP_W / 2, chipY + CHIP_H / 2 - 4);
    // Status row under the name: "ALLOWED" / "LOCKED 🔒".
    ctx.font = '8px "Share Tech Mono", ui-monospace, monospace';
    ctx.fillStyle = allowed ? COLORS.solved : 'rgba(125, 138, 163, 0.6)';
    ctx.fillText(allowed ? 'ALLOWED' : 'LOCKED', chipX + CHIP_W / 2, chipY + CHIP_H / 2 + 9);
    chipX += CHIP_W + CHIP_GAP;
  }
  cursorY += CHIP_H + 18;

  // Win condition — its own line.
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.font = '10px "Share Tech Mono", ui-monospace, monospace';
  ctx.fillStyle = COLORS.muted;
  ctx.fillText('WIN CONDITION', innerX, cursorY);
  cursorY += 14;
  ctx.font = '13px "Share Tech Mono", ui-monospace, monospace';
  ctx.fillStyle = COLORS.solved;
  const winLines = wrapToWidth(ctx, level.briefing.winCondition, innerW);
  for (const line of winLines) {
    ctx.fillText(line, innerX, cursorY);
    cursorY += 16;
  }

  // Action row — Start (right, primary) + Cancel (left, secondary).
  drawBriefingButton(ctx, BRIEFING_START_BTN, 'Start ▷', COLORS.solved, true);
  drawBriefingButton(ctx, BRIEFING_CANCEL_BTN, 'Cancel', COLORS.muted, false);
}

/** A compact line-wrap helper — greedy, ctx font-sensitive. */
function wrapToWidth(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const candidate = line ? `${line} ${w}` : w;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = w;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** Briefing-card primary / secondary action button — bigger than the close chip. */
function drawBriefingButton(
  ctx: CanvasRenderingContext2D,
  box: { x: number; y: number; w: number; h: number },
  label: string,
  color: string,
  primary: boolean,
): void {
  ctx.fillStyle = primary ? color : '#101a30';
  ctx.fillRect(box.x, box.y, box.w, box.h);
  ctx.strokeStyle = color;
  ctx.lineWidth = primary ? 2 : 1.4;
  ctx.strokeRect(box.x + 0.5, box.y + 0.5, box.w - 1, box.h - 1);
  ctx.fillStyle = primary ? '#06101f' : color;
  ctx.font = '16px "Chakra Petch", system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, box.x + box.w / 2, box.y + box.h / 2);
}

// ============================================================================
// Sprint 63 — end-of-attempt Solved / Try-again overlay.
// ----------------------------------------------------------------------------
// Stamped over the playfield once the puzzle wave terminates. Wins show par
// stats + Next / Back; losses show Reset / Back. Both consume input via
// `puzzleResultHit` so the player cannot click through to the playfield
// behind the overlay.
// ============================================================================

const RESULT_PANEL = { x: 130, y: 130, w: 540, h: 340 } as const;
const RESULT_PRIMARY_BTN = {
  x: RESULT_PANEL.x + RESULT_PANEL.w / 2 + 12,
  y: RESULT_PANEL.y + RESULT_PANEL.h - 56,
  w: 180,
  h: 40,
} as const;
const RESULT_BACK_BTN = {
  x: RESULT_PANEL.x + RESULT_PANEL.w / 2 - 12 - 180,
  y: RESULT_PANEL.y + RESULT_PANEL.h - 56,
  w: 180,
  h: 40,
} as const;

/** Hit-test result for the win / lose overlay. */
export type PuzzleResultHit =
  | { kind: 'primary' }
  | { kind: 'back' }
  | null;

/**
 * Hit-test the puzzle Win / Lose overlay. Returns the action — primary is
 * "Next level" on a win, "Reset attempt" on a loss; back is always "Back to
 * level select". `null` for a click in the dead zone (modal absorption).
 */
export function puzzleResultHit(x: number, y: number): PuzzleResultHit {
  if (inRect(x, y, RESULT_PRIMARY_BTN)) return { kind: 'primary' };
  if (inRect(x, y, RESULT_BACK_BTN)) return { kind: 'back' };
  return null;
}

/**
 * Draw the puzzle Win / Lose overlay. `won === true` renders the Solved!
 * variant with par stats + Next level; otherwise the Try Again variant with
 * Reset attempt. The orchestrator gates this on `state.mode === 'puzzle'` +
 * `phase === 'won' | 'lost'`.
 */
export function drawPuzzleResult(
  ctx: CanvasRenderingContext2D,
  levelId: string,
  won: boolean,
  stats: {
    towersUsed: number;
    goldSpent: number;
    livesRemaining: number;
    startingLives: number;
  },
  hasNextLevel: boolean,
): void {
  const level = getPuzzleLevel(levelId);
  if (!level) return;
  const index = PUZZLES.findIndex((l) => l.id === levelId);
  const accent = won ? COLORS.solved : '#ff5b6e';

  // Scrim + panel.
  ctx.fillStyle = COLORS.scrim;
  ctx.fillRect(0, 0, CONFIG.canvas.width, CONFIG.canvas.height);
  ctx.fillStyle = COLORS.panel;
  ctx.fillRect(RESULT_PANEL.x, RESULT_PANEL.y, RESULT_PANEL.w, RESULT_PANEL.h);
  ctx.strokeStyle = accent;
  ctx.lineWidth = 2;
  ctx.strokeRect(
    RESULT_PANEL.x + 0.5,
    RESULT_PANEL.y + 0.5,
    RESULT_PANEL.w - 1,
    RESULT_PANEL.h - 1,
  );

  const cx = RESULT_PANEL.x + RESULT_PANEL.w / 2;
  const top = RESULT_PANEL.y;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.font = '11px "Share Tech Mono", ui-monospace, monospace';
  ctx.fillStyle = COLORS.muted;
  ctx.fillText(won ? 'PUZZLE COMPLETE' : 'PUZZLE FAILED', cx, top + 22);

  // Hero — "SOLVED!" / "TRY AGAIN".
  ctx.font = '42px "Chakra Petch", system-ui, sans-serif';
  ctx.fillStyle = accent;
  ctx.fillText(won ? 'SOLVED!' : 'TRY AGAIN', cx, top + 44);

  // Level name caption.
  ctx.font = '14px "Chakra Petch", system-ui, sans-serif';
  ctx.fillStyle = COLORS.text;
  ctx.fillText(
    `P${index >= 0 ? index : '?'}  ·  ${level.briefing.name}`,
    cx,
    top + 100,
  );

  // Stats block.
  ctx.font = '12px "Share Tech Mono", ui-monospace, monospace';
  const statsY = top + 140;
  ctx.fillStyle = COLORS.muted;
  ctx.fillText('TOWERS', cx - 140, statsY);
  ctx.fillText('GOLD SPENT', cx, statsY);
  ctx.fillText('LIVES LEFT', cx + 140, statsY);
  ctx.font = '20px "Chakra Petch", system-ui, sans-serif';
  ctx.fillStyle = COLORS.text;
  ctx.fillText(String(stats.towersUsed), cx - 140, statsY + 16);
  ctx.fillText(`${stats.goldSpent}g`, cx, statsY + 16);
  ctx.fillText(
    `${stats.livesRemaining} / ${stats.startingLives}`,
    cx + 140,
    statsY + 16,
  );

  // Action row.
  let primaryLabel: string;
  if (won) primaryLabel = hasNextLevel ? 'Next level ▷' : 'Replay';
  else primaryLabel = 'Reset attempt';
  drawBriefingButton(ctx, RESULT_PRIMARY_BTN, primaryLabel, accent, true);
  drawBriefingButton(ctx, RESULT_BACK_BTN, 'Back to select', COLORS.accent, false);
}
